import { Injectable } from '@angular/core';
import { BehaviorSubject, first, Observable, ReplaySubject, share } from 'rxjs';
import * as config from '../shared/config/app.config';
import { GET_MAKE_PAYMENT, PaymentsMods } from '../shared/config/app.config';
import { BaseService } from '../shared/services/base.service';
import { map, tap } from 'rxjs/operators';
import { PaymentInfoResponse } from '../shared/models/payment-info.model';
import { MakePaymentApi, PaymentConfirmation } from '../shared/models/make-payment.model';

const INVOICES_API_CACHE_TIMEOUT = 300 * 1000;

@Injectable({
  providedIn: 'root',
})
export class MakeAPaymentService {
  private readonly invoicesData = new BehaviorSubject<any>(false); // behaviour subject containing the parent data of invoices & credit
  invoicesData$ = this.invoicesData.asObservable();

  private readonly creditsData = new BehaviorSubject<any>(false); // behaviour subject containing the parent data of invoices & credit
  creditsData$ = this.creditsData.asObservable();

  emails =  new BehaviorSubject<any>(false);
  emails$ = this.emails.asObservable();
  cachedInvoices$: Observable<any>;
  cachedInvoicesv2$: Observable<any>;
  cachedCredits$: Observable<any>;
  cacheTimeout: ReturnType<typeof setTimeout>;
  creditsCacheTimeout: ReturnType<typeof setTimeout>;
  invoicesCacheTimeout: ReturnType<typeof setTimeout>;
  cachedInvoicesApiParams: any = {};
  cachedCreditsApiParams: any = {};

  constructor(private readonly baseService: BaseService) {}

  getCachedInvoices(params): Observable<any> {

    if (!this.cachedInvoices$) {
      clearTimeout(this.cacheTimeout);

      this.cachedInvoices$ = this.baseService.get(config.GET_INVOICES, params)
        .pipe(
          (source$) => {
            const subject = new ReplaySubject(1);

            return source$.pipe(
              share({
                connector: () => subject,
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true,
              }),
            );
          }
        );

      this.cacheTimeout = setTimeout(()=> this.clearCachedInvoices(), INVOICES_API_CACHE_TIMEOUT);
    }

    return this.cachedInvoices$;
  }

  getCachedInvoicesV2(accountId: string, pageSize = 10, currentPage = 0): Observable<MakePaymentApi> {
    const apiParams = {
      accountId: accountId,
      fields: 'DEFAULT',
      documentType: 'invoices',
      pageSize: pageSize,
      currentPage: currentPage,
    };

    if (!this.cachedInvoicesv2$ || JSON.stringify(apiParams) !== JSON.stringify(this.cachedInvoicesv2$)) {
      clearTimeout(this.invoicesCacheTimeout);
      this.cachedInvoicesApiParams = apiParams;

      this.cachedInvoicesv2$ = this.baseService.get(GET_MAKE_PAYMENT, apiParams)
        .pipe(
          (source$) => {
            const subject = new ReplaySubject(1);

            return source$.pipe(
              share({
                connector: () => subject,
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true,
              }),
              tap(invoices => {
                this.invoicesData.next(invoices);
              }),
            );
          }
        );

      this.invoicesCacheTimeout = setTimeout(()=> this.clearCachedInvoices(), INVOICES_API_CACHE_TIMEOUT);
    }

    return this.cachedInvoicesv2$;
  }

  getCachedCredits(accountId: string, pageSize = 10, currentPage = 0): Observable<MakePaymentApi> {
    const apiParams = {
      accountId: accountId,
      fields: 'DEFAULT',
      documentType: 'credits',
      pageSize: pageSize,
      currentPage: currentPage,
    };

    if (!this.cachedCredits$ || JSON.stringify(apiParams) !== JSON.stringify(this.cachedInvoicesv2$)) {
      clearTimeout(this.creditsCacheTimeout);

      this.cachedCreditsApiParams = apiParams;

      this.cachedCredits$ = this.baseService.get(GET_MAKE_PAYMENT, apiParams)
        .pipe(
          (source$) => {
            const subject = new ReplaySubject(1);

            return source$.pipe(
              share({
                connector: () => subject,
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true,
              }),
              tap(credits => {
                this.creditsData.next(credits);
              }),

            );
          }
        );

      this.creditsCacheTimeout = setTimeout(()=> this.clearCachedCredits(), INVOICES_API_CACHE_TIMEOUT);
    }

    return this.cachedCredits$;
  }

  clearCachedInvoices() {
    this.cachedInvoices$ = null;
  }

  clearCachedCredits(): void {
    this.cachedCredits$ = null;
  }

  public downloadPdf(params): void {
    this.baseService
      .getPdf(config.DOWNLOAD_INVOICE_CREDIT_PDF, { invoiceNumber: params })
      .subscribe((res) => {
              if (res) {
                  const file = new Blob([res], {type: 'application/pdf'});
                  const fileURL = URL.createObjectURL(file);
                  window.open(fileURL);
              }
        // const file = new Blob([res.body], { type: 'pdf' });
        // const fileURL = URL.createObjectURL(file);
        // window.open(fileURL);
        //saveAs(new Blob([res.body], { type: 'pdf' }), 'data.pdf'); this works
        //  const file = new Blob([res.body], { type: 'application/pdf' });
        //saveAs(file, 'text.pdf');
        // const fileURL = URL.createObjectURL(file);
        // window.open(fileURL);
      },
          (error) => {} );
  }
  public getCardDetails(params): Observable<PaymentInfoResponse> {
    return this.baseService.get(config.GET_CARD_DETAILS, params);
  }
  public saveCreditDetails(userName, params) {
    return this.baseService.patch(
      `${config.USER_ACCOUNT}${userName}/payment-confirmation-credit`,
      params
    );
  }

  public makeCreditCardPayment(userName, params) {
    return this.baseService.post(
      `${config.USER_ACCOUNT}${userName}/ccinvoice-payment`,
      params
    );
  }

  public getAccountId(userName): Observable<any> {
    return this.baseService.get(`${config.USER_ACCOUNT}${userName}/b2bUnits`);
  }
  getEmails(userId, b2bUnitID): void {
    this.emails.next(false);

    this.baseService
      .get(
        `${config.USER_ACCOUNT}${userId}/customerEmails?b2bUnitId=${b2bUnitID}`
      )
      .subscribe((res) => {
        if (res) {
          this.emails.next(res);
        }
      });
  }

  makeCombinePayment(userName: string, params): Observable<PaymentConfirmation> {
    return this.baseService.patch(
      `${config.USER_ACCOUNT}${userName}/payments/payment-confirmation?fields=DEFAULT`,
      params
    );
  }

  getPaymentModes(): Observable<any[]> {
    return this.baseService
      .get(`${config.GET_PAYMENTMODES}`)
      .pipe(
        first(res => res?.paymentModes),
        map((res: any) => res.paymentModes.map(mode => mode.code).filter(mode => mode !== PaymentsMods.BILL_ME_LATER))
      )
  }
}
